import { Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useStudioAuthenticatedContext } from '../../../../../context/StudioContext';
import * as styles from './DashboardStatsSection.styles';

interface StatCardProps {
  value: string;
  label: string;
  changePercentage: string;
  percentageChange: number;
  comparisonText: string;
}

const StatCard = React.memo(
  ({ value, label, changePercentage, percentageChange, comparisonText }: StatCardProps) => {
    const isNegative = percentageChange < 0;
    return (
      <Card elevation={0} sx={styles.statCard}>
        <CardContent sx={styles.cardContent}>
          <Box>
            <Typography variant="h4" color="text.primary" sx={styles.valueTypography}>
              {value}
            </Typography>

            <Typography variant="body1" color="text.primary" sx={styles.labelTypography}>
              {label}
            </Typography>
          </Box>

          <Box sx={styles.comparisonContainer}>
            <Box sx={isNegative ? styles.changeBadgeError : styles.changeBadgeSuccess}>
              {changePercentage}
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={styles.comparisonTextTypography}
            >
              {comparisonText}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  },
);

StatCard.displayName = 'StatCard';

// Format number with k suffix (e.g., 10000 -> "10k")
const formatNumber = (num: number): string => {
  if (num >= 1000) {
    const k = Math.floor(num / 1000);
    return `${k}k`;
  }
  return num.toString();
};

// Format month name from date string (YYYY-MM-DD) to short month name (e.g., "Oct")
const formatMonthName = (dateString: string): string => {
  const [year, month] = dateString.split('-').map(Number);
  // Create date in local timezone to avoid UTC parsing issues
  const date = new Date(year, month - 1, 1); // month is 0-indexed in Date constructor
  return date.toLocaleDateString('en-US', { month: 'short' });
};

export const DashboardStatsSection = () => {
  const { backendClient, selectedAppSet } = useStudioAuthenticatedContext();
  const [totalPlaysData, setTotalPlaysData] = useState<{
    currentMonthPlays: number;
    previousMonthPlays: number;
    percentageChange: number;
    currentMonthStart: string;
    previousMonthStart: string;
  } | null>(null);
  const [activeUsersData, setActiveUsersData] = useState<{
    currentMonthUsers: number;
    previousMonthUsers: number;
    percentageChange: number;
    currentMonthStart: string;
    previousMonthStart: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!selectedAppSet) {
        setError('No application selected');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch both stats in parallel
        const [totalPlaysResponse, activeUsersResponse] = await Promise.all([
          backendClient.getTotalPlaysStats({
            appId: selectedAppSet.productionApp.id,
          }),
          backendClient.getActiveUsersStats({
            appId: selectedAppSet.productionApp.id,
          }),
        ]);

        if (!totalPlaysResponse || !activeUsersResponse) {
          setError('Invalid response from server');
          setTotalPlaysData(null);
          setActiveUsersData(null);
          return;
        }

        setTotalPlaysData({
          currentMonthPlays: totalPlaysResponse.current_month_plays || 0,
          previousMonthPlays: totalPlaysResponse.previous_month_plays || 0,
          percentageChange: totalPlaysResponse.percentage_change || 0,
          currentMonthStart: totalPlaysResponse.current_month_start || '',
          previousMonthStart: totalPlaysResponse.previous_month_start || '',
        });

        setActiveUsersData({
          currentMonthUsers: activeUsersResponse.current_month_users || 0,
          previousMonthUsers: activeUsersResponse.previous_month_users || 0,
          percentageChange: activeUsersResponse.percentage_change || 0,
          currentMonthStart: activeUsersResponse.current_month_start || '',
          previousMonthStart: activeUsersResponse.previous_month_start || '',
        });
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'An error occurred while fetching stats data',
        );
        setTotalPlaysData(null);
        setActiveUsersData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [backendClient, selectedAppSet]);

  // Format percentage change with sign
  const formatPercentageChange = (percentage: number): string => {
    const sign = percentage >= 0 ? '+' : '';
    return `${sign}${Math.round(percentage * 100)}%`;
  };

  // Build stats array
  const stats = React.useMemo(() => {
    const statsArray = [];

    // Active users stat
    if (loading || error || !activeUsersData) {
      statsArray.push({
        value: loading ? '...' : '0',
        label: 'Active users',
        changePercentage: '0%',
        percentageChange: 0,
        comparisonText: 'Loading...',
      });
    } else {
      const currentMonthName = formatMonthName(activeUsersData.currentMonthStart);
      const previousMonthName = formatMonthName(activeUsersData.previousMonthStart);
      const previousUsersFormatted = formatNumber(activeUsersData.previousMonthUsers);

      statsArray.push({
        value: formatNumber(activeUsersData.currentMonthUsers),
        label: 'Active users',
        changePercentage: formatPercentageChange(activeUsersData.percentageChange),
        percentageChange: activeUsersData.percentageChange,
        comparisonText: `${currentMonthName} vs. ${previousMonthName}: ${previousUsersFormatted}`,
      });
    }

    // Total plays stat
    if (loading || error || !totalPlaysData) {
      statsArray.push({
        value: loading ? '...' : '0',
        label: 'Total plays',
        changePercentage: '0%',
        percentageChange: 0,
        comparisonText: 'Loading...',
      });
    } else {
      const currentMonthName = formatMonthName(totalPlaysData.currentMonthStart);
      const previousMonthName = formatMonthName(totalPlaysData.previousMonthStart);
      const previousPlaysFormatted = formatNumber(totalPlaysData.previousMonthPlays);

      statsArray.push({
        value: formatNumber(totalPlaysData.currentMonthPlays),
        label: 'Total plays',
        changePercentage: formatPercentageChange(totalPlaysData.percentageChange),
        percentageChange: totalPlaysData.percentageChange,
        comparisonText: `${currentMonthName} vs. ${previousMonthName}: ${previousPlaysFormatted}`,
      });
    }

    return statsArray;
  }, [loading, error, activeUsersData, totalPlaysData]);

  if (loading) {
    return (
      <Box sx={styles.statsContainer}>
        <Card elevation={0} sx={styles.statCard}>
          <CardContent sx={styles.cardContent}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress size={40} />
            </Box>
          </CardContent>
        </Card>
        <Card elevation={0} sx={styles.statCard}>
          <CardContent sx={styles.cardContent}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress size={40} />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={styles.statsContainer}>
      {stats.map((stat, index) => (
        <StatCard
          key={`stat-${index}`}
          value={stat.value}
          label={stat.label}
          changePercentage={stat.changePercentage}
          percentageChange={stat.percentageChange}
          comparisonText={stat.comparisonText}
        />
      ))}
    </Box>
  );
};
